import { useCallback, useMemo } from 'react';
import { isEmpty, pick } from 'lodash';
import { translate } from '@td/shared_utils';
import { useMutation } from '@apollo/client';

import toastr from '../../../toastr';
import CREATE_DRUG_LIST_MAPS from '../../mutations/create-drug-list-maps.graphql';
import DRUG_SUMMARY from '../../queries/drug-summary.graphql';
import { SERVICE_LINE_DRUG_LIST_NAME } from './medication-availability';
import { DRUG_SUMMARY_DRUG_ID_TYPE } from '.';
import { getManagedStates, isMedicationAvailable } from '../../utils/drug-list-map';

const useMedicationAvailabilityEditForm = ({ serviceLine, drugListMaps, refStates, onComplete, drugId }) => {
  const initialValues = useMemo(
    () => ({
      medicationAvailable: isMedicationAvailable(drugListMaps, serviceLine),
      managedStates: getManagedStates(drugListMaps, refStates)
    }),
    [drugListMaps, serviceLine, refStates]
  );

  const [createDrugListMaps, { data, error }] = useMutation(CREATE_DRUG_LIST_MAPS, {
    update(
      cache,
      {
        data: {
          createDrugListMaps: { drugListMaps: createdDrugListMaps, errors }
        }
      }
    ) {
      if (!isEmpty(errors)) {
        return;
      }

      const drugSummaryQueryOptions = {
        query: DRUG_SUMMARY,
        variables: { drugId, drugIdType: DRUG_SUMMARY_DRUG_ID_TYPE }
      };

      const drugSummary = cache.readQuery(drugSummaryQueryOptions);

      if (isEmpty(drugSummary)) {
        return;
      }

      const newDrugListMaps = [
        ...drugSummary.drugListMaps.filter(
          drugListMap => drugListMap.drugList.name !== SERVICE_LINE_DRUG_LIST_NAME[serviceLine]
        ),
        ...createdDrugListMaps
      ];

      cache.writeQuery({
        ...drugSummaryQueryOptions,
        data: {
          ...drugSummary,
          drugListMaps: newDrugListMaps
        }
      });
    }
  });

  const buildDrugListMapsForSubmission = useCallback(
    (managedStates, medicationAvailable) => {
      if (managedStates.length > 0) {
        return managedStates.map(managedState => pick(managedState, ['stateCd']));
      }

      if (
        (['general_medical', 'dermatology', 'primary_care'].includes(serviceLine) && !medicationAvailable) ||
        (serviceLine === 'mental_health' && medicationAvailable)
      ) {
        return [{ stateCd: null }];
      }

      return [];
    },
    [serviceLine]
  );

  const onSubmit = ({ managedStates, medicationAvailable }) =>
    createDrugListMaps({
      variables: {
        drugId,
        drugListName: SERVICE_LINE_DRUG_LIST_NAME[serviceLine],
        drugListMaps: buildDrugListMapsForSubmission(managedStates, medicationAvailable)
      }
    }).then(
      ({
        data: {
          createDrugListMaps: { errors }
        }
      }) => {
        if (!isEmpty(errors)) {
          return;
        }

        toastr.success(
          translate(null, 'medication_formularies.details.availability.edit_form.notifications', 'edit_success')
        );

        onComplete();
      }
    );

  return {
    initialValues,
    onSubmit,
    data,
    error
  };
};

export default useMedicationAvailabilityEditForm;
